/**
 * Guided setup wizard helpers (tracker item 75, phase 2): the stepper's
 * wording, where it resumes, and the once-per-session first-login redirect.
 */

import * as types from '../api/types';

export const SETUP_PATH = '/setup';

/** Router state the stepper puts on its "Open full settings page" links; the settings pages show a way back. */
export const FROM_SETUP_STATE = { fromSetup: true } as const;

export function cameFromSetup(state: unknown): boolean {
  return typeof state === 'object' && state !== null && (state as { fromSetup?: unknown }).fromSetup === true;
}

/** A short explanation of each step, shown above its buttons. */
export const SETUP_STEP_HELP: Record<types.SetupStepKey, string> = {
  company_profile: 'Your company name and contact details appear on invoices, estimates and emails. Add your name and an address, phone number or email.',
  timezone_hours: 'Your timezone and working hours drive scheduling and the dispatch board. Check them under Scheduling on the Company page, then mark this step done.',
  tax_rate: 'Add the sales tax rate you charge, so invoices and estimates can calculate tax.',
  payment_terms: 'Choose how long customers have to pay (your default payment terms) under Billing on the Company page, then mark this step done.',
  items: 'Add the services and products you sell, with prices, so you can put them on jobs, estimates and invoices.',
  price_chart: 'A price chart prices a service from the size of a property. Skip it if you price by hand.',
  users: 'Invite your office staff and technicians so they can sign in with their own accounts.',
  email_sender: 'Connect and verify an email sender, then turn notifications on, so invoices, reminders and updates can be emailed.',
  square: 'Connect your card processor so customers can pay invoices online.',
  portal: 'Let customers see their invoices and visits in an online account. Skip it if you do not need one yet.',
};

export function stepStateLabel(step: types.SetupStep): string {
  if (step.done) return 'Done';
  return step.skipped ? 'Skipped' : 'To do';
}

/** Index of the first step that is neither done nor skipped (optional steps included), or steps.length when none. */
export function firstUnresolvedIndex(steps: readonly types.SetupStep[]): number {
  const index = steps.findIndex((s) => !s.done && !s.skipped);
  return index === -1 ? steps.length : index;
}

// ---- first-login redirect ----

const REDIRECT_KEY_PREFIX = 'crm.setupRedirect.';
/** Fallback when sessionStorage is unavailable: still once per page load, so the redirect can never loop. */
const redirectedInMemory = new Set<string>();

/** Whether the setup redirect already happened for this user in this browser session. */
export function setupRedirectDone(userId: string): boolean {
  if (redirectedInMemory.has(userId)) return true;
  try {
    return window.sessionStorage.getItem(REDIRECT_KEY_PREFIX + userId) === '1';
  } catch {
    return false;
  }
}

export function markSetupRedirectDone(userId: string): void {
  redirectedInMemory.add(userId);
  try {
    window.sessionStorage.setItem(REDIRECT_KEY_PREFIX + userId, '1');
  } catch {
    // storage blocked: the in-memory flag still stops a loop
  }
}

/** Test helper: forget the in-memory flags (sessionStorage is cleared by the test). */
export function resetSetupRedirectMemory(): void {
  redirectedInMemory.clear();
}
